// Help-centre questions and answers — pure data with no framework or
// environment access, so both the Next.js site (/help, homepage FAQ) and the
// offline register PWA (bundled for offline reading) import the same text.
//
// Every answer describes behaviour that exists in this codebase (API error
// messages, PWA outbox rules, portal draft saving) — keep them in step when
// that behaviour changes.

// Answers are arrays of blocks: a string is a paragraph, { list: [...] } is a
// bullet list and { steps: [...] } a numbered list.
export const HELP_CATEGORIES = [
  {
    id: 'getting-started',
    title: 'Getting started',
    summary: 'What PharmaCare does and how a pharmacy goes live.',
    questions: [
      {
        id: 'what-is-pharmacare',
        q: 'What is PharmaCare PMS?',
        a: [
          'PharmaCare PMS is pharmacy management software built in Ghana. It brings point of sale, inventory with batches and expiry dates, drug information, staff roles and reporting into one system that runs on your pharmacy’s own computers.',
          'Around it sit three web tools: the pharmacy portal (sales, analytics, stock, a web till and stock counts), the offline register for phones, and the license check.',
        ],
      },
      {
        id: 'move-records',
        q: 'Will you help us move from our current records?',
        a: [
          'Yes. We install the system, import your drug list, set up users, receipts, printing and backups, and train your team before you go live.',
        ],
      },
      {
        id: 'setup-time',
        q: 'How long does it take to go live?',
        a: [
          'It depends on the size of your drug list and how many computers you run. We agree a go-live date with you during the walkthrough, once we have seen your setup.',
        ],
      },
      {
        id: 'hardware',
        q: 'What computers and printers do we need?',
        a: [
          'Usually the computers you already have. During the walkthrough we check your machines, local network and receipt printer, and tell you if anything needs to change before installation.',
        ],
      },
      {
        id: 'multi-pc',
        q: 'Can several computers share the same stock?',
        a: [
          'Yes. One computer acts as the server and holds the pharmacy database; the other counters connect to it over your local network, so every station sells from the same stock. Smaller pharmacies can run a single standalone computer.',
        ],
      },
    ],
  },
  {
    id: 'offline',
    title: 'Offline & internet',
    summary: 'What keeps working without a connection, and what needs one.',
    questions: [
      {
        id: 'offline-works',
        q: 'Does PharmaCare keep working when the internet goes down?',
        a: [
          'Yes. The core system runs on your own computers with a local database, so staff keep selling, dispensing and printing receipts. Online services pick up again when the connection returns.',
        ],
      },
      {
        id: 'needs-internet',
        q: 'What needs an internet connection?',
        a: [
          'Day-to-day selling does not. These do:',
          { list: [
            'The desktop app’s periodic license check (roughly every 30 days).',
            'Activating a license on a new computer, which our team approves.',
            'Syncing users and stock to the cloud for the offline register and stock-count portal.',
            'Uploading offline-register sales and submitting stock counts.',
          ] },
        ],
      },
      {
        id: 'power-outage',
        q: 'What can we do during a power cut?',
        a: [
          'Use the offline register on a charged phone or laptop. It sells from the latest stock saved on that device, records every payment, and uploads the sales once there is a connection again. Your administrator then reviews them in the desktop app.',
        ],
      },
    ],
  },
  {
    id: 'offline-register',
    title: 'Offline register',
    summary: 'The phone register for outages: sign-in, receipts and syncing.',
    questions: [
      {
        id: 'register-what',
        q: 'What is the offline register?',
        a: [
          'A web app for staff phones and laptops, at /pwa. When the main computer is off or unreachable, staff can keep ringing up sales from the stock saved on their device. Sales wait on the phone and upload automatically, then appear under Pending Offline Sales in the desktop app for approval before they touch your stock records.',
        ],
      },
      {
        id: 'register-eligibility',
        q: 'Who can use the offline register?',
        a: [
          { list: [
            'Pharmacies on a Monthly or Annual license. Trial licenses cannot sign in.',
            'Offline Mode must be switched on in the desktop app (Admin → Network & Sync).',
            'Staff sign in with their usual PharmaCare username and password, so their account must be synced from the desktop first.',
          ] },
        ],
      },
      {
        id: 'register-first-signin',
        q: 'Why must I sign in while online?',
        a: [
          'Signing in downloads your pharmacy’s stock to the device. After that the register works without a connection, but a sign-in lasts about 11 hours. Sign in at the start of each shift while you still have data or Wi-Fi.',
          'The saved stock is flagged as out of date once it is more than 7 days old. Sign in online again to refresh it.',
        ],
      },
      {
        id: 'register-install',
        q: 'How do I install the register on my phone?',
        a: [
          { steps: [
            'Android (Chrome): open the register, tap the ⋮ menu, then Install app or Add to Home screen.',
            'iPhone (Safari): open the register, tap Share, then Add to Home Screen. This only works in Safari, not inside WhatsApp or other in-app browsers.',
          ] },
          'Once installed, it opens full-screen like any other app.',
        ],
      },
      {
        id: 'register-statuses',
        q: 'What do Queued, Synced and Rejected mean?',
        a: [
          { list: [
            'Queued: saved on this phone and waiting to upload. The register retries on its own every 30 seconds and as soon as the connection returns.',
            'Synced: received by PharmaCare and waiting for your administrator’s approval in the desktop app.',
            'Rejected: our server refused the sale (for example, the account was deactivated). The receipt stays on the phone; tell your administrator.',
          ] },
        ],
      },
      {
        id: 'register-receipts',
        q: 'How does the customer get a receipt?',
        a: [
          'After each sale the register creates a PDF receipt and opens your phone’s share sheet, so you can send it by WhatsApp or print it. Every receipt stays on the device and can be shared again from Receipts.',
        ],
      },
      {
        id: 'register-hold',
        q: 'Can I pause a sale and serve someone else?',
        a: [
          'Yes. Tap Hold to park the current sale, then Resume it later. Prices and stock are refreshed when you resume, and anything that has since run out is removed.',
        ],
      },
      {
        id: 'register-units',
        q: 'Can I sell by the box instead of by the tablet?',
        a: [
          'Yes. When a medicine has a pack size, you can switch the line between the base unit (for example Tab), the pack (for example Box ×30) and a Pack of ten boxes. The price follows the base price times the number of units.',
        ],
      },
      {
        id: 'register-signout',
        q: 'What happens to waiting sales if I sign out?',
        a: [
          'They stay on the phone and upload after the next sign-in on that device. Do not clear the browser’s data while sales are still queued.',
        ],
      },
    ],
  },
  {
    id: 'pharmacy-portal',
    title: 'Pharmacy portal',
    summary: 'Sales, analytics, stock and selling from any browser.',
    questions: [
      {
        id: 'portal-what',
        q: 'What is the pharmacy portal?',
        a: [
          'The pharmacy’s PharmaCare system on any phone or laptop, at /portal. Staff sign in with the pharmacy name and their desktop username and password, and see:',
          { list: [
            'Overview: today’s takings against the same day last week, work waiting for approval, and stock that needs reordering or is close to expiry.',
            'Analytics: revenue over time against the period before, profit and margin, top products, payment methods, busiest hours and sales by staff.',
            'Sell: a web till that prices every unit exactly like the desktop.',
            'Sales, stock, stock counts, the team’s figures and the license.',
          ] },
        ],
      },
      {
        id: 'portal-turn-on',
        q: 'How do we turn it on?',
        a: [
          { steps: [
            'On the main computer (the server PC if you have several), open Admin → Network & Sync.',
            'Switch on “Online pharmacy portal”. The first upload sends the last 13 months of sales and takes a few minutes.',
            'Staff can then sign in at /portal. After that, each sale reaches the portal within about a minute while the computer is on and online.',
          ] },
          'The portal comes with Annual and Monthly licences.',
        ],
      },
      {
        id: 'portal-roles',
        q: 'What can each person see?',
        a: [
          'Exactly what their role and permissions allow in the desktop app. Counter staff sell, see stock and their own sales; pharmacists also see every sale waiting for approval; owners and auditors see analytics, profit and the team. Cost prices and profit only show to people allowed to see costs. Change a role on the desktop and the portal follows within minutes.',
        ],
      },
      {
        id: 'portal-data',
        q: 'What leaves our computer?',
        a: [
          'Sales (amounts, products, quantities, cashier, payment method and time), stock levels, expiry dates, prices and staff logins (passwords stay scrambled). Customer names, phone numbers, notes and prescription references never leave the computer.',
        ],
      },
      {
        id: 'portal-web-sales',
        q: 'What happens to a sale made in the portal?',
        a: [
          'It goes to the main computer for approval, the same way offline register sales do, and shows as waiting under Sales until then. On approval the desktop deducts the stock earliest expiry first and the sale joins the books. The receipt number printed in the portal is the one the sale keeps.',
        ],
      },
    ],
  },
  {
    id: 'stock-count',
    title: 'Stock-count portal',
    summary: 'Counting shelves from a phone and sending figures for review.',
    questions: [
      {
        id: 'count-what',
        q: 'What is the stock-count portal?',
        a: [
          'The stock count in the pharmacy portal (/portal/count), where staff record what is physically on the shelf. Counts go to your pharmacist, who reviews and approves them in the desktop app before any stock figure changes.',
        ],
      },
      {
        id: 'count-signin',
        q: 'Which details do I use to sign in?',
        a: [
          'The same pharmacy name, username and password you use in the desktop app. The pharmacy name must match your license exactly.',
        ],
      },
      {
        id: 'count-partial',
        q: 'Do I have to count everything at once?',
        a: [
          'No. Submit what you have counted and come back for the rest. Zero is a valid count for an empty shelf; leave an item blank if you have not counted it.',
        ],
      },
      {
        id: 'count-draft',
        q: 'What if my phone dies or I lose connection mid-count?',
        a: [
          'Your counts are saved on the device as you type and to the cloud a moment later. Sign back in and the latest draft is restored, even on a different device.',
        ],
      },
      {
        id: 'count-after-submit',
        q: 'What happens after I submit?',
        a: [
          'Your pharmacist sees the counts in the desktop app, compares them with the system figures and approves any changes. Nothing is adjusted until then.',
        ],
      },
    ],
  },
  {
    id: 'licensing',
    title: 'Licenses & renewals',
    summary: 'Checking status, renewing, and moving to a new computer.',
    questions: [
      {
        id: 'license-check',
        q: 'Can we track our license and renewal?',
        a: [
          'Yes. The license status page shows your license type, expiry date, days remaining and the last time your system checked in. Use the pharmacy name and registration number from your license.',
        ],
      },
      {
        id: 'license-types',
        q: 'What license options are there?',
        a: [
          'Trial, Monthly and Annual. The offline register needs a Monthly or Annual license.',
        ],
      },
      {
        id: 'license-renew',
        q: 'How do we renew?',
        a: [
          'Contact us on WhatsApp or by email before the expiry date and we will arrange the renewal. Renewing early avoids any interruption.',
        ],
      },
      {
        id: 'license-new-pc',
        q: 'Can we move our license to a new computer?',
        a: [
          'Yes. Activating a license on a different computer needs approval from the PharmaCare team, so contact us before you switch machines.',
        ],
      },
    ],
  },
  {
    id: 'security',
    title: 'Security & data',
    summary: 'Where your records live and who can see them.',
    questions: [
      {
        id: 'data-where',
        q: 'Where is our pharmacy data stored?',
        a: [
          'Your main records live in the local database on your pharmacy’s computers. Some data is synced to the PharmaCare cloud to power the web tools: staff accounts (passwords stored as secure hashes), your drug list and stock levels, submitted counts and offline sales, and license check-ins.',
        ],
      },
      {
        id: 'access-control',
        q: 'How is access controlled for different staff?',
        a: [
          'Administrators assign roles and permissions, so pharmacists, dispensers, cashiers and other staff only see the screens and actions their job needs. Actions are recorded in an activity trail.',
        ],
      },
      {
        id: 'backups',
        q: 'Do you set up backups?',
        a: [
          'Yes. Backups are configured as part of installation, so your records can be restored if a computer fails.',
        ],
      },
      {
        id: 'lost-phone',
        q: 'A staff phone was lost. What should we do?',
        a: [
          'Deactivate that user in the desktop app and sync users. Sign-ins stop working, sales from that account are refused, and any open session ends within 12 hours.',
        ],
      },
    ],
  },
  {
    id: 'pricing',
    title: 'Pricing',
    summary: 'How quotes and plans work.',
    questions: [
      {
        id: 'cost',
        q: 'How much does it cost?',
        a: [
          'Pricing depends on your branches, computers, staff and support needs, so we quote after a short walkthrough rather than forcing you into a fixed tier. Trial, monthly and annual licenses are available.',
        ],
      },
      {
        id: 'trial',
        q: 'Can we try it first?',
        a: [
          'Yes. Trial licenses are available. Ask for one when you book your walkthrough.',
        ],
      },
    ],
  },
  {
    id: 'troubleshooting',
    title: 'Sign-in problems',
    summary: 'What each sign-in message means and how to fix it.',
    questions: [
      {
        id: 'ts-pharmacy',
        q: 'It says no pharmacy with that name is registered.',
        a: [
          'The pharmacy name must match your license exactly, including spelling and spacing. Ask your administrator for the name as it appears on the license.',
        ],
      },
      {
        id: 'ts-user',
        q: 'It says my username does not exist at this pharmacy.',
        a: [
          'New or renamed accounts only reach the web tools after a sync. Ask your administrator to sync users from the desktop app, then try again.',
        ],
      },
      {
        id: 'ts-password',
        q: 'My password is not accepted.',
        a: [
          'Use the same password as the desktop app. If it was changed recently, or the message says no password has been synced yet, your administrator needs to sync users from the desktop app.',
        ],
      },
      {
        id: 'ts-deactivated',
        q: 'It says my account has been deactivated.',
        a: [
          'Your administrator has switched the account off. They can re-enable it in the desktop app and sync users.',
        ],
      },
      {
        id: 'ts-offline-mode',
        q: 'It says Offline Mode is not enabled for this pharmacy.',
        a: [
          'The offline register needs a Monthly or Annual license, with Offline Mode switched on in the desktop app under Admin → Network & Sync. Contact us to change your license.',
        ],
      },
      {
        id: 'ts-too-many',
        q: 'It says there have been too many attempts.',
        a: [
          'Sign-in is paused for a short while after repeated failures to protect your account. Wait about 15 minutes, check your details, and try again.',
        ],
      },
    ],
  },
]

const ALL_QUESTIONS = HELP_CATEGORIES.flatMap((category) =>
  category.questions.map((question) => ({ ...question, category: category.id, categoryTitle: category.title })),
)

export function getQuestions(ids) {
  return ids.map((id) => ALL_QUESTIONS.find((question) => question.id === id)).filter(Boolean)
}

export function answerText(blocks) {
  return blocks
    .map((block) => (typeof block === 'string' ? block : (block.list || block.steps || []).join(' ')))
    .join(' ')
}
